/**
 * CC Cart Handler
 * Handles AJAX cart operations for the sidecart
 * - Remove items without redirect
 * - Quantity updates with sync
 */
(function() {
  'use strict';

  const CartHandler = {
    init: function() {
      this.attachEventListeners();
    },

    attachEventListeners: function() {
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.handleRemove(removeBtn);
          return;
        }

        const qtyBtn = e.target.closest('[data-cart-qty-btn]');
        if (qtyBtn) {
          setTimeout(() => this.handleQuantityChange(qtyBtn), 50);
        }
      });

      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
        if (form) {
          const submitBtn = form.querySelector('[type="submit"], .js-btn-add-to-cart');
          if (submitBtn) {
            this.setAddToCartLoading(submitBtn, true);
          }
        }
      });
    },

    getLineKey: function(element) {
      if (element.hasAttribute('data-line-key')) {
        return element.getAttribute('data-line-key');
      }
      const cartItem = element.closest('[data-line-key]');
      return cartItem ? cartItem.getAttribute('data-line-key') : null;
    },

    getVariantId: function(element) {
      const cartItem = element.closest('[data-cart-variant-id]');
      return cartItem ? cartItem.getAttribute('data-cart-variant-id') : null;
    },

    getLineIndex: function(element) {
      if (element.hasAttribute('data-line-index')) {
        return parseInt(element.getAttribute('data-line-index'));
      }
      const cartItem = element.closest('[data-line-index]');
      return cartItem ? parseInt(cartItem.getAttribute('data-line-index')) : null;
    },

    getCurrentQuantity: function(element) {
      const cartItem = element.closest('[data-cart-variant-id]');
      if (!cartItem) return 1;

      const qtyInput = cartItem.querySelector('.js-input-quantity');
      return qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    },

    setItemLoadingState: function(element, loading) {
      const cartItem = element.closest('[data-cart-variant-id]');
      if (!cartItem) return;

      if (loading) {
        cartItem.style.opacity = '0.5';
        cartItem.setAttribute('data-loading', 'true');
        // DO NOT set pointer-events: none - it causes clickability issues
      } else {
        cartItem.style.opacity = '';
        cartItem.removeAttribute('data-loading');
      }
    },

    clearAllLoadingStates: function() {
      document.querySelectorAll('[data-cart-variant-id]').forEach(item => {
        item.style.opacity = '';
        item.style.pointerEvents = '';
        item.removeAttribute('data-loading');
      });
    },

    setAddToCartLoading: function(btn, loading) {
      if (!btn) return;

      if (loading) {
        btn._originalText = btn.textContent;
        btn.disabled = true;
        btn.style.opacity = '0.7';
        btn.style.cursor = 'wait';
        // Find the text span and update it
        const textSpan = btn.querySelector('span.cc-inline-block') || btn.querySelector('span:not(.sr-only):not([aria-hidden])');
        if (textSpan) {
          textSpan.textContent = 'ADDING...';
        } else {
          btn.textContent = 'ADDING...';
        }
      } else {
        btn.disabled = false;
        btn.style.opacity = '';
        btn.style.cursor = '';
        if (btn._originalText) {
          const textSpan = btn.querySelector('span.cc-inline-block') || btn.querySelector('span:not(.sr-only):not([aria-hidden])');
          if (textSpan) {
            textSpan.textContent = btn._originalText;
          } else {
            btn.textContent = btn._originalText;
          }
        }
      }
    },

    handleRemove: function(removeBtn) {
      const lineKey = this.getLineKey(removeBtn);

      if (!lineKey) {
        console.error('[CartHandler] No line key found');
        return;
      }

      this.setItemLoadingState(removeBtn, true);

      this.updateCartByKey(lineKey, 0)
        .then(() => this.refreshMiniCart())
        .catch(() => this.setItemLoadingState(removeBtn, false));
    },

    handleQuantityChange: function(qtyBtn) {
      const lineKey = this.getLineKey(qtyBtn);
      const newQuantity = this.getCurrentQuantity(qtyBtn);

      if (!lineKey) {
        console.error('[CartHandler] No line key found');
        return;
      }

      this.setItemLoadingState(qtyBtn, true);

      this.updateCartByKey(lineKey, newQuantity)
        .then(() => this.refreshMiniCart())
        .catch(() => this.setItemLoadingState(qtyBtn, false));
    },

    updateCartByKey: function(lineKey, quantity) {
      const updates = {};
      updates[lineKey] = quantity;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
      .then(response => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      });
    },

    updateCart: function(payload) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(response => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      });
    },

    refreshMiniCart: function() {
      return fetch('/?sections=cc-header')
        .then(response => {
          if (!response.ok) throw new Error('Failed to fetch cart section');
          return response.json();
        })
        .then(sections => {
          const newCartHTML = sections['cc-header'];
          if (!newCartHTML) {
            this.clearAllLoadingStates();
            return;
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(newCartHTML, 'text/html');

          const newContents = doc.querySelector('[data-cart-contents]');
          const currentContents = document.querySelector('[data-cart-contents]');
          if (newContents && currentContents) {
            currentContents.replaceChildren(...newContents.cloneNode(true).childNodes);
          }

          const newCount = doc.querySelector('[data-cart-item-count]');
          if (newCount) {
            document.querySelectorAll('[data-cart-item-count]').forEach(el => {
              el.textContent = newCount.textContent;
            });
          }

          const newSummary = doc.querySelector('.mini-cart-summary');
          const currentSummary = document.querySelector('.mini-cart-summary');
          if (newSummary && currentSummary) {
            currentSummary.replaceChildren(...newSummary.cloneNode(true).childNodes);
            const isEmpty = parseInt(newCount?.textContent || '0') === 0;
            isEmpty ? currentSummary.classList.add('hidden') : currentSummary.classList.remove('hidden');
          }

          const emptyMessage = document.querySelector('[data-cart-empty]');
          if (emptyMessage) {
            const isEmpty = parseInt(newCount?.textContent || '0') === 0;
            isEmpty ? emptyMessage.classList.remove('hidden') : emptyMessage.classList.add('hidden');
          }

          this.clearAllLoadingStates();
        })
        .catch(() => {
          this.clearAllLoadingStates();
        });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartHandler.init());
  } else {
    CartHandler.init();
  }
})();
